import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useFetch } from "@/lib/useFetch";
import { Seo } from "@/lib/seo";
import { Loading } from "@/components/States";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { VenueCard, EventCard } from "@/components/Cards";

const HERO = "https://images.unsplash.com/photo-1508863702330-2eece3ea08cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function GameDayPage() {
  const { data, loading } = useFetch("/gameday", []);

  return (
    <>
      <Seo title="Hotelstrips GameDay — Where to stay for every game & event"
        description="Browse leagues, teams, venues and upcoming events. Find the best areas to stay, transit and hotels for your gameday trip."
        image={HERO} path="/gameday" />

      <section className="relative py-24 sm:py-32 flex items-center">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full">
          <div className="overline text-primary mb-3">Hotelstrips GameDay</div>
          <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter text-white max-w-3xl leading-tight">
            Pick a league, a team or a venue. We'll handle the where-to-stay.
          </h1>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
        <Breadcrumbs items={[{ label: "GameDay" }]} />

        {loading ? <Loading /> : (
          <>
            {/* Sports / leagues */}
            <div className="mb-14">
              <div className="overline text-primary mb-4">Browse by league</div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {data?.leagues?.filter((l) => l.active).map((l) => (
                  <Link key={l.slug} to={`/${l.slug}`} data-testid={`league-${l.slug}`} className="rounded-xl bg-card border border-black/5 card-shadow p-6 transition-transform duration-300 hover:-translate-y-1">
                    <div className="font-heading text-2xl font-extrabold tracking-tight text-ink">{l.abbreviation}</div>
                    <div className="text-sm text-muted-foreground mt-1">{l.name}</div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Venues */}
            <div className="mb-14">
              <div className="flex items-end justify-between mb-5">
                <h2 className="font-heading text-3xl font-extrabold tracking-tighter text-ink">Featured venues</h2>
              </div>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {data?.venues?.map((v) => <VenueCard key={v.slug} venue={v} />)}
              </div>
            </div>

            {/* Upcoming events */}
            <div>
              <h2 className="font-heading text-3xl font-extrabold tracking-tighter text-ink mb-5">Upcoming events</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {data?.events?.map((e) => <EventCard key={e.slug} event={e} />)}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
